import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import classNames from 'classnames';
import { Container } from 'components/base';
import { CloudinaryPicture } from 'components/rich';

import { Picture, imageShape } from '../landing/shared';
import PdpIcon from '../product/pdpIcon';

// Greeting-only parts of /custom-photo-greeting-cards (docs/greeting-cards-page.md): the holiday
// cross-link band and the "stationery and mail" stages (photos in postage stamp frames, SKU card
// pieces and a postmark) from "Custom Photo Greeting Cards — Redesign" in design/website.pen.

export const HOLIDAY_CARDS_HREF = '/custom-photo-holiday-cards';

// Seasonal intent belongs to the holiday page. Shown in the holiday season only.
export const HolidayLinkBand = ({ onClick }) => (
  <div className="hc-band">
    <Container className="hc-band-inner">
      <p className="hc-band-copy">
        <PdpIcon name="snowflake" size={18} />
        <span>
          Sending holiday cards this year? See our Christmas, New Year and Hanukkah photo cards.
        </span>
      </p>
      <Link href={HOLIDAY_CARDS_HREF} className="hc-band-link" onClick={onClick}>
        Custom Photo Holiday Cards
        <PdpIcon name="arrowRight" size={16} className="lnd-desk" />
      </Link>
    </Container>
  </div>
);

HolidayLinkBand.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const WAVES = 'q5-6 10 0t10 0q5-6 10 0t10 0q5-6 10 0t10 0q5-6 10 0t10 0q5-6 10 0t10 0';

// The ring scales with its box, so the lettering stays in proportion at every size.
const Postmark = ({ waves }) => (
  <svg
    className="gc-postmark-svg"
    viewBox={waves ? '0 0 216 120' : '0 0 120 120'}
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="60" cy="60" r="59" className="gc-postmark-ring" />
    <text x="60" y="47" className="gc-postmark-small">
      PARABO PRESS
    </text>
    <text x="60" y="65" className="gc-postmark-mid">
      Sent with love
    </text>
    <text x="60" y="81" className="gc-postmark-small">
      BY MAIL
    </text>
    {waves && (
      <path d={`M116 40${WAVES}m-100 18${WAVES}m-100 18${WAVES}`} className="gc-postmark-waves" />
    )}
  </svg>
);

Postmark.propTypes = {
  waves: PropTypes.bool,
};

// Card pieces crop the SKU image to the card itself. Portrait foil SKUs crop tighter to hide the
// GOLD FOIL badge baked into the image.
const cropOf = (piece) => {
  if (piece.shape === 'landscape') return 'landscape';
  return piece.skuId.includes('-foil') ? 'portrait-foil' : 'portrait';
};

const Piece = ({ piece, sizes }) => {
  if (piece.kind === 'postmark') return <Postmark waves={piece.waves} />;
  if (piece.kind === 'stamp') {
    return (
      <span className="gc-stamp-photo">
        <Picture image={piece.image} sizes={sizes} alt="" priority={piece.priority} />
      </span>
    );
  }
  return (
    <CloudinaryPicture
      prefix="sku/"
      imageKey={piece.skuId}
      alt=""
      priority={piece.priority}
      loading={piece.priority ? undefined : 'lazy'}
    />
  );
};

Piece.propTypes = {
  piece: PropTypes.object.isRequired,
  sizes: PropTypes.string,
};

// Pieces are placed in design pixels of the stage frame, [x, y, width, rotation], and turned into
// percentages so the stage scales with its column. `phone` places them in the mobile frame where
// it differs, `deskOnly` leaves them out on phones.
const place = (at, [width, height], suffix = '') => ({
  [`--x${suffix}`]: `${(at[0] / width) * 100}%`,
  [`--y${suffix}`]: `${(at[1] / height) * 100}%`,
  [`--w${suffix}`]: `${(at[2] / width) * 100}%`,
  [`--r${suffix}`]: `${at[3]}deg`,
});

export const MailStage = ({ size, phoneSize, pieces, className }) => (
  <div
    className={classNames('gc-stage', className)}
    style={{
      '--ratio': `${size[0]} / ${size[1]}`,
      '--ratio-phone': `${(phoneSize ?? size)[0]} / ${(phoneSize ?? size)[1]}`,
    }}
    aria-hidden="true"
  >
    {pieces.map((piece) => (
      <span
        key={piece.skuId ?? piece.image?.imageKey ?? piece.kind}
        className={classNames(
          'gc-piece',
          `gc-piece--${piece.kind}`,
          piece.kind === 'card' && `gc-crop--${cropOf(piece)}`,
          piece.deskOnly && 'gc-piece--desk',
        )}
        style={{
          ...place(piece.at, size),
          ...(piece.phone ? place(piece.phone, phoneSize ?? size, '-phone') : {}),
        }}
      >
        <Piece piece={piece} sizes={`(min-width: 992px) ${piece.at[2]}px, 50vw`} />
      </span>
    ))}
  </div>
);

const placement = PropTypes.arrayOf(PropTypes.number);

MailStage.propTypes = {
  size: placement.isRequired,
  phoneSize: placement,
  pieces: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.oneOf(['stamp', 'card', 'postmark']).isRequired,
      at: placement.isRequired,
      phone: placement,
      deskOnly: PropTypes.bool,
      image: imageShape,
      skuId: PropTypes.string,
      shape: PropTypes.oneOf(['portrait', 'landscape']),
      waves: PropTypes.bool,
      priority: PropTypes.bool,
    }),
  ).isRequired,
  className: PropTypes.string,
};

// "Upload your own design" link on the right of the tab bar (desktop only).
export const UploadLink = ({ onClick }) => (
  <a href="#upload-your-own" className="hc-deadline" onClick={onClick}>
    <PdpIcon name="upload" size={16} />
    Upload your own design
  </a>
);

UploadLink.propTypes = {
  onClick: PropTypes.func.isRequired,
};
